import Vue from 'vue'
import Vuex from 'vuex'
import _ from 'lodash'
import client from '@/lib/client'
import utils from '@/lib/utils'
import Analytics from '@/lib/analytics'

Vue.use(Vuex)

/*
  ATTENTION

  Before making *any* changes to this file, please read the official guide
  https://vuex.vuejs.org/guide/state.html
*/

export default new Vuex.Store({
  // The global state tree
  state: {
    authInfo: {},
    defaultApiToken: {},
    unreadBanners: null,
    unreadNotifications: null,
    accountInfo: {},
    isFetchingAccountInfo: false,
    multiModelAck: false,
    models: {},
    selectedModelId: null,
    userProfiles: {},
    signalsUserProfiles: {},
    currentRoundInfo: {},
    latestRoundInfo: {},
    tournamentOverview: {},
    signalsTournamentOverview: null,
    signalsSubmissionsDiagnostics: {},
    nmrPriceInfo: {},
    snackBar: {
      open: false,
      msg: '',
      time: 6000,
    },
    selectedProfileView: 'submission',
    signalsSelectedProfileView: 'submission',
    signalsSelectedRoundNumber: 0,
    selectedRoundNumber: 0,
    isDarkMode: false,
    isDashboardNavMinimized: false,
    modelsLimit: 100,
    isFetchingPickles: true,
    pickles: [],
    hasPickleError: false,
    pickleDiagnostics: {},
  },

  // Component not re-rendering? Read the reactivity rules
  // https://vuejs.org/v2/guide/reactivity.html
  mutations: {
    setAuthInfo(state, authInfo) {
      if (authInfo.username) {
        Analytics.setUser(authInfo.username)
      }
      state.authInfo = authInfo
    },
    setDarkMode(state, darkMode) {
      state.isDarkMode = darkMode
    },
    setDashboardNavMinimized(state, minimized) {
      state.isDashboardNavMinimized = minimized
    },
    setNmrPriceInfo(state, nmrPriceInfo) {
      state.nmrPriceInfo = nmrPriceInfo
    },
    setSnackBar(state, snackBar) {
      state.snackBar = snackBar
    },
    setAccountInfo(state, accountInfo) {
      state.accountInfo = accountInfo
      if (accountInfo.models) {
        for (const model of accountInfo.models) {
          Vue.set(state.models, model.id, model)
        }
      }
    },
    setIsFetchingAccountInfo(state, status) {
      state.isFetchingAccountInfo = status
    },
    setUserProfile(state, { username, userProfile }) {
      // updating maps have special reactivity rules
      Vue.set(state.userProfiles, userProfile.username, userProfile)
      Vue.set(state.userProfiles, username, userProfile)
    },
    setSignalsUserProfile(state, { username, userProfile }) {
      // updating maps have special reactivity rules
      Vue.set(state.signalsUserProfiles, username, userProfile)
    },
    setCurrentRoundInfo(state, currentRoundInfo) {
      state.currentRoundInfo = currentRoundInfo
    },
    setLatestRoundInfo(state, latestRoundInfo) {
      state.latestRoundInfo = latestRoundInfo
    },
    setTournamentOverview(state, tournamentOverview) {
      state.tournamentOverview = tournamentOverview
    },
    setSignalsTournamentOverview(state, signalsTournamentOverview) {
      state.signalsTournamentOverview = signalsTournamentOverview
    },
    setSignalsSubmissionsDiagnostics(state, payload) {
      // updating maps have special reactivity rules
      Vue.set(
        state.signalsSubmissionsDiagnostics,
        payload.submissionId,
        payload.signalsSubmissionDiagnostics,
      )
    },
    setDefaultApiToken(state, defaultApiToken) {
      state.defaultApiToken = defaultApiToken
    },
    setUnreadBanners(state, unreadBanners) {
      state.unreadBanners = unreadBanners
    },
    setUnreadNotifications(state, unreadNotifications) {
      state.unreadNotifications = unreadNotifications
    },
    setSelectedProfileView(state, view) {
      state.selectedProfileView = view
    },
    setSignalsSelectedProfileView(state, view) {
      state.signalsSelectedProfileView = view
    },
    setSelectedRoundNumber(state, number) {
      state.selectedRoundNumber = number
    },
    setSignalsSelectedRoundNumber(state, number) {
      state.signalsSelectedRoundNumber = number
    },
    setSelectedModelId(state, modelId) {
      state.selectedModelId = modelId
    },
    setMultiModelAck(state, ack) {
      state.multiModelAck = ack
    },
    setModelsLimit(state, limit) {
      state.modelsLimit = limit
    },
    setIsFetchingPickles(state, status) {
      state.isFetchingPickles = status
    },
    setPickles(state, pickles) {
      state.pickles = pickles
    },
    setHasPickleError(state, hasError) {
      state.hasPickleError = hasError
    },
    setPickleDiagnostics(state, pickleDiagnostics) {
      state.pickleDiagnostics = {
        ...state.pickleDiagnostics,
        ...pickleDiagnostics,
      }
    },
  },

  // Actions are dispatched from components and commit
  // mutations to modify state
  // All actions are debounced at 1 per 500ms, which means
  // each component is free to fetch any data it needs on created
  actions: _.mapValues(
    {
      async fetchAccountInfo(context) {
        if (!context.state.isFetchingAccountInfo) {
          context.commit('setIsFetchingAccountInfo', true)

          const accountInfo = await client.getAccount()
          const curModels = accountInfo.models.filter(
            model => model.tournament === utils.getTournamentNumber(),
          )
          accountInfo.models = curModels

          context.commit('setAccountInfo', accountInfo)

          const modelsLimit = await client.getMaxModelSlots(
            utils.getTournamentNumber(),
          )
          context.commit('setModelsLimit', modelsLimit)
        }
      },
      async fetchV3UserProfile(context, args) {
        let username = args.username
        let tournament = args.tournament
        const userProfile = await client.getV3UserProfile({
          username,
          tournament,
        })
        if (!userProfile) {
          throw new Error('Profile does not exist')
        }
        context.commit('setUserProfile', { username, userProfile })
      },
      async fetchAllModelUploads(context) {
        context.commit('setIsFetchingPickles', true)
        let pickles = await client.getModelUploads({
          includeTriggerStatuses: true,
        })
        let hasPickleError = pickles
          .map(
            pickle =>
              pickle.triggerStatus === 'model_failed' ||
              pickle.triggerStatus === 'submission_failed' ||
              pickle.validationStatus === 'failed' ||
              pickle.diagnosticsStatus === 'failed',
          )
          .includes(true)
        context.commit('setHasPickleError', hasPickleError)
        context.commit('setPickles', pickles)
        context.commit('setIsFetchingPickles', false)
      },
      async fetchCurrentRoundInfo(context, tournament) {
        const currentRoundInfo = await client.getCurrentRoundInfo(tournament)
        context.commit('setCurrentRoundInfo', currentRoundInfo)
      },
      async fetchLatestRoundInfo(context, tournament) {
        const latestRoundInfo = await client.getLatestRoundInfo(tournament)
        context.commit('setLatestRoundInfo', latestRoundInfo)
      },
      async fetchSignalsSubmissionDiagnostics(context, submissionId) {
        const signalsSubmissionDiagnostics = await client.getSignalsSubmissionDiagnostics(
          submissionId,
        )
        context.commit('setSignalsSubmissionsDiagnostics', {
          submissionId,
          signalsSubmissionDiagnostics,
        })
      },
      /* eslint-enable */
      async fetchDefaultApiToken(context) {
        const defaultApiToken = await client.getDefaultApiToken()
        context.commit('setDefaultApiToken', defaultApiToken)
      },
      async fetchUnreadBanners(context) {
        const unreadBanners = await client.getUnreadBanners()
        context.commit('setUnreadBanners', unreadBanners)
      },
      async fetchUnreadNotifications(context) {
        const unreadNotifications = await client.getUnreadNotifications()
        context.commit('setUnreadNotifications', unreadNotifications)
      },
      loadSelectedModelId(context) {
        const selectedModelId = localStorage.getItem('selectedModelId')
        context.commit('setSelectedModelId', selectedModelId)
      },
      loadAccountInfo(context) {
        // todo does not work
        const accountInfo = client.getAccount()
        context.commit('setAccountInfo', accountInfo)
      },
      changeSelectedModelId(context, selectedModelId) {
        localStorage.setItem('selectedModelId', selectedModelId)
        context.commit('setSelectedModelId', selectedModelId)
      },
      loadDarkMode(context) {
        let darkMode = localStorage.getItem('darkmode')
        if (darkMode == undefined) {
          localStorage.setItem('darkmode', 'false')
        } else if (darkMode == 'false') {
          darkMode = false
        }
        context.commit('setDarkMode', !!darkMode)
      },
      loadDashboardNavMinimized(context) {
        let dashboardNavMinimized = localStorage.getItem(
          'dashboardNavMinimized',
        )
        if (dashboardNavMinimized == undefined) {
          localStorage.setItem('dashboardNavMinimized', 'false')
        } else if (dashboardNavMinimized == 'false') {
          dashboardNavMinimized = false
        }
        context.commit('setDashboardNavMinimized', !!dashboardNavMinimized)
      },
      saveDarkMode(context, darkMode) {
        localStorage.setItem('darkmode', !!darkMode)
        context.commit('setDarkMode', !!darkMode)
        if (darkMode) {
          document.body.classList.add('darkmode')
        } else {
          document.body.classList.remove('darkmode')
        }
      },
      saveDashboardNavMinimized(context, minimized) {
        localStorage.setItem('dashboardNavMinimized', !!minimized)
        context.commit('setDashboardNavMinimized', !!minimized)
      },
      loadAuthInfo(context) {
        const token = localStorage.getItem('auth-token')
        const username = localStorage.getItem('username')
        context.commit('setAuthInfo', { token, username })
      },
      saveAuthInfo(context, { token, username }) {
        localStorage.setItem('auth-token', token)
        localStorage.setItem('username', username)
        localStorage.setItem('invalidLocalAuthInfo', false)
        context.commit('setAuthInfo', { token, username })
      },
      updateUsername(context, username) {
        localStorage.setItem('username', username)
        const token = localStorage.getItem('auth-token')
        context.commit('setAuthInfo', { token, username })
      },
      loadMultiModelAck(context) {
        const ack = localStorage.getItem('multiModelAck')
        context.commit('setMultiModelAck', ack)
      },
      saveMultiModelAck(context, ack) {
        localStorage.setItem('multiModelAck', ack)
        context.commit('setMultiModelAck', ack)
      },
      clearAuthInfo(context) {
        localStorage.removeItem('auth-token')
        localStorage.removeItem('username')
        localStorage.removeItem('invalidLocalAuthInfo')

        // clear all local storage just to be safe
        localStorage.clear()

        // also clear the snackbar
        context.dispatch('closeSnackBar')

        context.commit('setAuthInfo', {})
      },
      clearAccountInfo(context) {
        context.commit('setAccountInfo', {})
      },
      async fetchPrices(context) {
        const latestNmrPrice = await client.latestNmrPrice()
        context.commit('setNmrPriceInfo', latestNmrPrice)
      },
      pollForPrices(context) {
        context.dispatch('fetchPrices')
        const five_min = 5 * 60 * 1000
        setInterval(() => {
          context.dispatch('fetchPrices')
        }, five_min)
      },
      showSnackBar(context, { msg, time = 6000 }) {
        context.commit('setSnackBar', { open: true, msg, time })
      },
      closeSnackBar(context) {
        context.commit('setSnackBar', { open: false, msg: '' })
      },
    },
    action => _.debounce(action, 500, { leading: true }),
  ),
  getters: {
    isLoggedIn: state => !!state.authInfo.token && !!state.authInfo.username,
    isAppSignals: utils.isAppSignals,
    isAppCryptoSignals: utils.isAppCryptoSignals,
    isMFAEnabled: state => state.accountInfo.mfaEnabled,
    isBeta: state => state.accountInfo.isBeta,
    isDarkMode: state => state.isDarkMode,
    isMultiModel: state => (state.accountInfo.models || []).length > 1,
    isMultiEnabled: (state, getters) =>
      state.multiModelAck || getters.isMultiModel,
    isDashboardNavMinimized: state => state.isDashboardNavMinimized,
    atModelLimit: state =>
      _.get(state, 'accountInfo.models', []).length >= state.modelsLimit,
    modelsRemaining: state =>
      state.modelsLimit - _.get(state, 'accountInfo.models', []).length,
    username: state => state.authInfo.username,
    modelNames: state => {
      if (state.accountInfo.models && state.accountInfo.models.length > 0) {
        return state.accountInfo.models.map(m => m.name)
      } else {
        return null
      }
    },
    defaultModel: state =>
      (state.accountInfo.models || []).length > 0
        ? state.accountInfo.models[0]
        : null,
    selectedModel: (state, getters) => {
      return _.get(state.models, state.selectedModelId) || getters.defaultModel
    },
    modelname: (state, getters) =>
      getters.selectedModel ? getters.selectedModel.name : null,
    nmrPrice: state => state.nmrPriceInfo.priceUsd,
    profile: (state, getters) => _.get(state.userProfiles, getters.modelname),
    signalsProfile: (state, getters) =>
      _.get(state.signalsUserProfiles, getters.modelname),
    unreadNotifications: state => state.unreadNotifications,
    isStaked: (state, getters) => {
      return (
        !!parseFloat(
          _.get(getters.selectedModel, 'v2Stake.latestValue', false),
        ) ||
        !!_.get(
          getters.selectedModel,
          'v2Stake.pendingV2ChangeStakeRequest',
          false,
        )
      )
    },
    isStakedSignals: (state, getters) => {
      return (
        !!_.get(getters.selectedModel, 'v2Stake.latestValue', false) ||
        !!_.get(
          getters.selectedModel,
          'v2Stake.pendingV2ChangeStakeRequest',
          false,
        )
      )
    },
    hasCredits: state => !!parseFloat(state.accountInfo.availableStakeCredit),
    creditsAvailable: state =>
      parseFloat(state.accountInfo.availableStakeCredit),
    activeStakes: (state, getters) =>
      getters.profile &&
      _(getters.profile.latestRoundPerformances)
        .filter(p => !p.roundResolved && p.selectedStakeValue && p.correlation)
        .sort((a, b) => b.roundNumber - a.roundNumber)
        .value(),
    signalsActiveStakes: (state, getters) =>
      getters.signalsProfile &&
      _(getters.signalsProfile.latestRoundPerformances)
        .filter(p => !p.roundResolved && p.selectedStakeValue && p.correlation)
        .sort((a, b) => b.roundNumber - a.roundNumber)
        .value(),
    currentPayoutSelection: (state, getters) =>
      _.get(getters.selectedModel, 'currentPayoutSelection'),
  },
})
